import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import { IconName, Text } from '@components/base'
import { SectionItem } from '@screens/Profile'
import { openUrl } from '@utils'
import { StyleService, useStyleSheet } from '@src/style/service'
import { clientConfigStoreStateSelector } from '@src/selectors/app'
import { Feature, useFeatureFlag } from '@src/components'
import { Analytics, CustomEventTypes } from '@src/config'
import { useVideoCallProductType } from '@src/hooks/useVideoCallProductType.ts'
import { useLazyScreenPreload } from '@src/navigation/utils'

interface ContactItem {
  icon: IconName
  label: string
  onPress: () => void
}

export const AccountRowsSection = () => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation()

  const dispatch = useDispatch()
  const config = useSelector(clientConfigStoreStateSelector)

  useLazyScreenPreload('SupportHub')

  const outcomesTrackingEnabled = useFeatureFlag(Feature.OutcomesTracking)
  const insuranceResubmissionEnabled = useFeatureFlag(Feature.InsuranceResubmission)
  const { isInsuranceVideoCallProductType } = useVideoCallProductType()

  const contactItems: ContactItem[] = [
    {
      icon: 'instagram-logo',
      label: 'Follow us on Instagram',
      onPress: () => openUrl('https://www.instagram.com/nutrisenseio/'),
    },
    {
      icon: 'facebook-logo',
      label: 'Join Facebook Group',
      onPress: () => openUrl('https://www.facebook.com/nutrisenseio/'),
    },
    {
      icon: 'trash-simple',
      label: 'Delete my account',
      onPress: () => openUrl(config.privacyCenterUrl),
    },
    {
      icon: 'sign-out',
      label: 'Sign out',
      onPress: () => dispatch({ type: 'app/logout' }),
    },
  ]

  return (
    <>
      <SectionItem
        hasBottomBorder
        icon="lifebuoy"
        onPress={() => {
          navigation.navigate('SupportHub')
        }}
      >
        <Text type="regular" style={styles.textGrow}>
          Get Help
        </Text>
      </SectionItem>
      {outcomesTrackingEnabled && (
        <SectionItem
          hasBottomBorder
          icon="calendar-plus"
          onPress={() => {
            Analytics.track(CustomEventTypes.OutcomesTrackingLabResultsTapped)
            navigation.navigate('LabResultsScreen')
          }}
        >
          <Text type="regular" style={styles.textGrow}>
            Lab Results
          </Text>
        </SectionItem>
      )}
      {insuranceResubmissionEnabled && isInsuranceVideoCallProductType && (
        <SectionItem
          hasBottomBorder
          icon="identification-card"
          onPress={() => {
            navigation.navigate('InsuranceStatus')
          }}
        >
          <Text type="regular" style={styles.textGrow}>
            Insurance Status
          </Text>
        </SectionItem>
      )}
      {contactItems.map(({ icon, label, onPress }, index) => (
        <SectionItem
          hasBottomBorder={index !== contactItems.length - 1}
          icon={icon}
          key={label}
          onPress={onPress}
        >
          <Text type="regular" style={styles.text}>
            {label}
          </Text>
        </SectionItem>
      ))}
    </>
  )
}

const themedStyles = StyleService.create({
  text: {
    flex: 1,
  },
  textGrow: {
    flex: 1,
  },
  textSecondary: {
    color: 'theme.text.secondary',
  },
  optedIn: {
    marginTop: 4,
    color: 'theme.success.base',
  },
  optedOut: {
    marginTop: 4,
    color: 'theme.disabled.text',
  },
})
